import React from 'react'
import clsx from 'clsx'
import { Card } from '@/components'
import { useAppStore } from '@/store/appStore'

type SettingsTab = 'general' | 'developer'

const TABS: { value: SettingsTab; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'developer', label: 'Developer' },
]

interface ToggleRowProps {
  title: string
  description: string
  checked: boolean
  onChange: (next: boolean) => void
  testId?: string
}

const ToggleRow: React.FC<ToggleRowProps> = ({ title, description, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-6">
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="mt-0.5 text-sm text-slate-500">{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-primary-600' : 'bg-slate-300'
      )}
    >
      <span
        className={clsx(
          'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}
      />
    </button>
  </div>
)

export const SettingsPage: React.FC = () => {
  const [tab, setTab] = React.useState<SettingsTab>('general')
  const devMode = useAppStore((state) => state.devMode)
  const setDevMode = useAppStore((state) => state.setDevMode)
  const sidebarCollapsed = useAppStore((state) => state.sidebarCollapsed)
  const setSidebarCollapsed = useAppStore((state) => state.setSidebarCollapsed)

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Settings</h1>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={tab === item.value}
            onClick={() => setTab(item.value)}
            className={clsx(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === item.value
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <Card className="max-w-2xl px-5 py-4">
        {tab === 'general' ? (
          <ToggleRow
            title="Collapse sidebar"
            description="Show only icons in the left navigation."
            checked={sidebarCollapsed}
            onChange={setSidebarCollapsed}
          />
        ) : (
          <ToggleRow
            title="Developer mode"
            description="Show diagnostics, interaction timelines, raw agent output and the Developer page."
            checked={devMode}
            onChange={setDevMode}
            testId="dev-mode-toggle"
          />
        )}
      </Card>
    </div>
  )
}
